import { useState, useEffect } from 'react';
import { useFirmCapabilities, CAPABILITY_ATLASSIAN_INTEGRATION } from '../hooks/useFirmCapabilities';
import { Lightbulb, FileText, Users, Sparkles, Loader2, Copy, Check, Wand2, ChevronDown, Layers, LayoutGrid } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';
import type { HierarchicalConfigItem } from '../types/phase1e';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Alert, AlertDescription } from './ui/alert';

interface ConsultantAIDiscoveryToolsProps {
  item: HierarchicalConfigItem;
  companyId: string;
  companyName: string;
  accessToken: string;
  /** Consulting firm id for capability checks; omit for legacy-permissive behavior. */
  consultingFirmId?: string | null;
}

export function ConsultantAIDiscoveryTools({
  item,
  companyId,
  companyName,
  accessToken,
  consultingFirmId,
}: ConsultantAIDiscoveryToolsProps) {
  const { hasCapability, ready: capabilitiesReady } = useFirmCapabilities(consultingFirmId, accessToken);
  /**
   * Strict gating (Phase 3B prep): only enable Jira-integration features once the firm
   * capabilities response has actually been received. While `capabilitiesReady` is false
   * the Analysis tab stays disabled and we render no denial alert — see denial branch
   * in the Analysis TabsContent below.
   */
  const showJiraIntegration =
    capabilitiesReady && hasCapability(CAPABILITY_ATLASSIAN_INTEGRATION);
  const jiraIntegrationDenied =
    capabilitiesReady && !hasCapability(CAPABILITY_ATLASSIAN_INTEGRATION);

  const [activeTab, setActiveTab] = useState<'prep' | 'analysis'>('prep');
  const [contextScope, setContextScope] = useState<'item' | 'category' | 'engagement'>('item');

  // Pre-meeting prep state
  const [talkingPoints, setTalkingPoints] = useState<string[]>([]);
  const [agenda, setAgenda] = useState('');
  const [suggestedAttendees, setSuggestedAttendees] = useState<any[]>([]);
  const [loadingPrep, setLoadingPrep] = useState(false);
  const [copiedAgenda, setCopiedAgenda] = useState(false);
  
  // Post-meeting analysis state
  const [meetingNotes, setMeetingNotes] = useState('');
  const [jiraConfigs, setJiraConfigs] = useState<any[]>([]);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  useEffect(() => {
    if (activeTab === 'analysis' && jiraIntegrationDenied) {
      setActiveTab('prep');
    }
  }, [activeTab, jiraIntegrationDenied]);

  const handleGeneratePreMeetingMaterials = async () => {
    try {
      setLoadingPrep(true);
      toast.info('Generating AI-powered meeting materials...');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/ai/pre-meeting-prep`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            companyName,
            configItemId: item.id,
            configItemName: item.name,
            configItemDescription: item.description,
            configItemCategory: item.category,
            contextScope: contextScope, // Pass scope to backend
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTalkingPoints(data.talkingPoints || []);
        setAgenda(data.agenda || '');
        setSuggestedAttendees(data.suggestedAttendees || []);
        toast.success('Meeting materials generated');
      } else {
        const error = await response.json();
        console.error('[PRE-MEETING PREP] Error response:', error);
        toast.error(error.error || 'Failed to generate materials');
      }
    } catch (error) {
      console.error('Error generating pre-meeting materials:', error);
      toast.error('Failed to generate meeting materials');
    } finally {
      setLoadingPrep(false);
    }
  };

  const handleCopyAgenda = () => {
    navigator.clipboard.writeText(agenda);
    setCopiedAgenda(true);
    toast.success('Agenda copied to clipboard');
    setTimeout(() => setCopiedAgenda(false), 2000);
  };

  const handleAnalyzeMeetingNotes = async () => {
    if (!meetingNotes.trim()) {
      toast.error('Please enter meeting notes or transcript');
      return;
    }

    try {
      setLoadingAnalysis(true);
      toast.info('Analyzing meeting notes and mapping to Jira configurations...');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/ai/analyze-meeting-notes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            companyName,
            configItemId: item.id,
            configItemName: item.name,
            configItemDescription: item.description,
            configItemCategory: item.category,
            meetingNotes,
            contextScope: contextScope, // Pass scope to backend
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setJiraConfigs(data.jiraConfigurations || []);
        setAnalysisComplete(true);
        toast.success(`Identified ${data.jiraConfigurations?.length || 0} Jira configurations`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to analyze notes');
      }
    } catch (error) {
      console.error('Error analyzing meeting notes:', error);
      toast.error('Failed to analyze meeting notes');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleApproveJiraConfigs = async () => {
    try {
      toast.info('Saving approved Jira configurations...');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/ai/approve-jira-configs`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            configItemId: item.id,
            jiraConfigurations: jiraConfigs,
          }),
        }
      );

      if (response.ok) {
        toast.success('Jira configurations saved and ready for auto-configuration');
        setMeetingNotes('');
        setJiraConfigs([]);
        setAnalysisComplete(false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save configurations');
      }
    } catch (error) {
      console.error('Error approving Jira configs:', error);
      toast.error('Failed to save configurations');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
        <Sparkles className="size-5 text-purple-600 mt-0.5" />
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-purple-900">AI Discovery Assistant</h3>
            
            {/* Context Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs border-purple-200 bg-white text-purple-700 hover:bg-purple-50">
                   Run against: {contextScope === 'item' ? 'This Requirement' : contextScope === 'category' ? 'Category' : 'Engagement'}
                   <ChevronDown className="size-3 ml-2 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>AI Context Scope</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setContextScope('item')}>
                  <FileText className="size-3.5 mr-2 text-gray-500" />
                  This Requirement
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setContextScope('category')}>
                  <Layers className="size-3.5 mr-2 text-gray-500" />
                  This Category
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setContextScope('engagement')}>
                  <LayoutGrid className="size-3.5 mr-2 text-gray-500" />
                  Entire Engagement
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-sm text-purple-800 mt-1">
            AI will analyze based on the selected scope.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="prep">
            <Lightbulb className="size-4 mr-2" />
            Pre-Meeting Prep
          </TabsTrigger>
          <TabsTrigger value="analysis" disabled={!showJiraIntegration}>
            <Wand2 className="size-4 mr-2" />
            Post-Meeting Analysis
          </TabsTrigger>
        </TabsList>

        {/* PRE-MEETING PREP TAB */}
        <TabsContent value="prep" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="size-5 text-amber-500" />
                Discovery Session Preparation
              </CardTitle>
              <CardDescription>
                Generate expert talking points, meeting agenda, and suggested attendees for your discovery session
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={handleGeneratePreMeetingMaterials} 
                disabled={loadingPrep}
                className="w-full"
              >
                {loadingPrep ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Generating Materials...
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4 mr-2" />
                    Generate Meeting Materials
                  </>
                )}
              </Button>

              {talkingPoints.length > 0 && (
                <div className="space-y-3">
                  <Separator />
                  
                  {/* Talking Points */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="size-4 text-amber-500" />
                      <h4 className="font-semibold">Expert Talking Points</h4>
                    </div>
                    <div className="space-y-2 pl-6">
                      {talkingPoints.map((point, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Badge variant="outline" className="mt-0.5">{idx + 1}</Badge>
                          <p className="text-sm flex-1">{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Suggested Attendees */}
                  {suggestedAttendees.length > 0 && (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Users className="size-4 text-blue-500" />
                          <h4 className="font-semibold">Suggested Attendees</h4>
                        </div>
                        <div className="grid gap-2 pl-6">
                          {suggestedAttendees.map((attendee, idx) => (
                            <div key={idx} className="border rounded-lg p-3 space-y-1">
                              <p className="font-medium text-sm">{attendee.role}</p>
                              <p className="text-xs text-muted-foreground">{attendee.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Separator />
                    </>
                  )}

                  {/* Meeting Agenda */}
                  {agenda && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="size-4 text-green-500" />
                          <h4 className="font-semibold">Meeting Agenda</h4>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCopyAgenda}
                        >
                          {copiedAgenda ? (
                            <>
                              <Check className="size-4 mr-2" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="size-4 mr-2" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <pre className="text-sm whitespace-pre-wrap font-sans">{agenda}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* POST-MEETING ANALYSIS TAB — Jira mapping requires Atlassian integration capability.
            While capabilities are still loading we render nothing in this tab to avoid
            flashing a denial alert on firms that will resolve as capable. */}
        <TabsContent value="analysis" className="space-y-4 mt-4">
          {jiraIntegrationDenied ? (
            <Alert>
              <AlertDescription>
                Post-meeting Jira configuration mapping is not enabled for this firm&apos;s capabilities.
              </AlertDescription>
            </Alert>
          ) : !showJiraIntegration ? null : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="size-5 text-purple-500" />
                  Meeting Notes Analysis
                </CardTitle>
                <CardDescription>
                  Paste your meeting notes or transcript to automatically extract and map Jira configurations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  placeholder="Paste your meeting notes or transcript here... 

Example:
- Client mentioned they need 3 approval stages for requests over $10K
- Department leads should be the first approvers
- CFO must approve anything over $25K
- Email notifications should go to approvers immediately
- They want a dashboard showing pending approvals..."
                  rows={12}
                  className="font-mono text-sm"
                />

                <Button 
                  onClick={handleAnalyzeMeetingNotes} 
                  disabled={loadingAnalysis || !meetingNotes.trim()}
                  className="w-full"
                >
                  {loadingAnalysis ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Analyzing Notes...
                    </>
                  ) : (
                    <>
                      <Sparkles className="size-4 mr-2" />
                      Analyze & Map to Jira
                    </>
                  )}
                </Button>

                {jiraConfigs.length > 0 && (
                  <div className="space-y-3">
                    <Separator />
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 font-medium">
                        AI identified {jiraConfigs.length} Jira configuration{jiraConfigs.length !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {jiraConfigs.map((config, idx) => (
                        <div key={idx} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge variant="secondary" className="mb-2">{config.type}</Badge>
                              <h4 className="font-semibold">{config.name}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
                            </div>
                          </div>
                          
                          {config.details && (
                            <div className="bg-gray-50 rounded p-3 mt-2">
                              <p className="text-xs font-medium text-gray-600 mb-1">Configuration Details:</p>
                              <pre className="text-xs whitespace-pre-wrap">
                                {JSON.stringify(config.details, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={handleApproveJiraConfigs}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Check className="size-4 mr-2" />
                      Approve & Save Configurations
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}